import type {
  CssVarsObject,
  PaletteColors,
  PaletteConfig,
  PaletteRgbColors,
  RicosTheme,
} from './../../../ricos-types/index.js';

import { FALLBACK_COLOR_DEFAULT } from '../../theme-constants.js';
import { COLORS, assertWixPalette, getColorValue, isRicosPalette } from '../palettes/index.js';
import {
  adaptForeground,
  canTransformToHex,
  toCssRgb,
  toHexFormat,
  toRgbTuple,
} from '../themeUtils.js';
import { isRicosPaletteRgb } from './identifiers.js';

type StringMap = { [key: string]: string | undefined };

const createCssVars = (
  colors: PaletteColors | PaletteRgbColors,
  config?: PaletteConfig
): CssVarsObject => {
  const { contentBgColor = false, focusActionColor, settingsActionColor } = config || {};
  const isPaletteRgb = isRicosPaletteRgb(colors);

  const {
    textColor,
    bgColor: backgroundColor,
    actionColor,
    fallbackColor = isPaletteRgb ? '0, 0, 0' : FALLBACK_COLOR_DEFAULT,
    disabledColor,
    textColorLow,
    textOnActionColor,
  } = colors;

  const parseColors = (obj: StringMap): CssVarsObject =>
    Object.keys(obj).reduce((acc: StringMap, key: string) => {
      const value = obj[key];
      if (value !== undefined) {
        const transformedValue =
          canTransformToHex(value) && isPaletteRgb ? toRgbTuple(toHexFormat(value)) : undefined;

        const colorValue = transformedValue !== undefined ? transformedValue : value;

        acc[key] = isPaletteRgb ? toCssRgb(colorValue) : colorValue;
        acc[`${key}Tuple`] = isPaletteRgb ? colorValue : toRgbTuple(colorValue);
      }
      return acc;
    }, {});

  const parsedValues = parseColors({
    textColor,
    actionColor,
    backgroundColor,
    fallbackColor,
    disabledColor,
    textColorLow,
    settingsActionColor,
    focusActionColor,
    textOnActionColor,
  });

  const bgColorContainer =
    !contentBgColor || backgroundColor === undefined
      ? undefined
      : isPaletteRgb
        ? toCssRgb(backgroundColor)
        : backgroundColor;

  const actionColorFallback = isPaletteRgb // not possible to adapt foreground to rgb values since it can be CSS var
    ? `rgb(${fallbackColor})`
    : adaptForeground(actionColor, fallbackColor);

  const actionColorFallbackTuple = isPaletteRgb
    ? fallbackColor
    : toRgbTuple(adaptForeground(actionColor, fallbackColor));

  return {
    ...parsedValues,
    ...(actionColorFallback && { actionColorFallback }),
    ...(actionColorFallbackTuple && { actionColorFallbackTuple }),
    ...(bgColorContainer && { bgColorContainer }),
  };
};

const extractColors = (palette: RicosTheme['palette']): PaletteColors => {
  if (Array.isArray(palette)) {
    assertWixPalette(palette);
    return {
      actionColor: getColorValue(palette, COLORS.ACTION_COLOR),
      bgColor: getColorValue(palette, COLORS.BG_COLOR),
      textColor: getColorValue(palette, COLORS.TEXT_COLOR),
      disabledColor: getColorValue(palette, COLORS.DISABLED_COLOR),
      textColorLow: getColorValue(palette, COLORS.TEXT_COLOR_LOW),
    };
  } else if (palette && (isRicosPalette(palette) || isRicosPaletteRgb(palette))) {
    return palette;
  }
  console.error('Unrecognized Palette object. Please refer to Ricos Theme Documentation');
  return {};
};

interface PaletteStrategyResult {
  paletteVarsObject: CssVarsObject;
  colors?: PaletteColors;
}

export default function createPalette(
  palette?: RicosTheme['palette'],
  config?: RicosTheme['paletteConfig']
): PaletteStrategyResult {
  if (!palette) {
    return { paletteVarsObject: {} };
  }
  const isPaletteRgb = isRicosPaletteRgb(palette);
  const colors = extractColors(palette);
  const transformedColors = transformColorsToHex(colors, isPaletteRgb);

  return { paletteVarsObject: createCssVars(transformedColors, config), colors: transformedColors };
}

function transformColorsToHex(colors: PaletteColors, isPaletteRgb = false): PaletteColors {
  return Object.entries(colors).reduce((acc, [key, value]) => {
    acc[key as $TSFixMeKey<PaletteColors>] =
      value && typeof value === 'string' && !isPaletteRgb && canTransformToHex(value)
        ? toHexFormat(value)
        : value;

    return acc;
  }, {} as PaletteColors);
}
